import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";
import { 
  MoreHorizontalIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";
import { Application } from "../../model_gen/application";

interface ApplicationsTableProps {
  applications: Application[];
  loading: boolean;
  sortConfig: SortConfig<Application>;
  onSort: (key: keyof Application) => void;
  onDelete: (applicationId: number) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
}

const ApplicationsTable: React.FC<ApplicationsTableProps> = ({
  applications,
  loading,
  sortConfig,
  onSort,
  onDelete,
  currentPage,
  setCurrentPage,
  pageSize,
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<Application>[] = [
    {
      key: "application_name",
      header: "Name",
      width: "w-[40%]",
      sortable: true,
      render: (app) => (
        <div className="font-gc-med text-foreground hover:text-maitai-green transition-colors">
          {app.application_name}
        </div>
      )
    },
    {
      key: "date_created",
      header: "Created",
      width: "w-[25%]",
      sortable: true,
      render: (app) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(app.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(app.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "last_activity",
      header: "Last Activity", 
      width: "w-[25%]",
      sortable: true,
      render: (app) => (
        <div className="space-y-1">
          <div className={cn(
            "text-sm font-gc-reg",
            app.last_activity > 0 ? "text-foreground" : "text-muted-foreground"
          )}>
            {formatDate(app.last_activity)}
          </div>
          <div className={cn(
            "text-xs",
            app.last_activity > 0 ? "text-muted-foreground" : "text-muted-foreground/60"
          )}>
            {formatTime(app.last_activity)}
          </div>
        </div>
      )
    },
    {
      key: "id",
      header: "",
      width: "w-[10%]",
      sortable: false,
      render: (app) => (
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              // Simple dropdown menu using native browser context menu
              const menu = document.createElement("div");
              menu.className = "absolute right-0 top-8 z-[9999] min-w-48 bg-popover border border-border rounded-md shadow-lg py-1";
              menu.innerHTML = `
                <button class="flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors text-red-600">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                  Delete
                </button>
              `;
              
              // Position and show menu
              const buttonRect = e.currentTarget.getBoundingClientRect();
              const container = e.currentTarget.parentElement;
              if (container) {
                container.appendChild(menu);
                
                // Add click handler
                const deleteButton = menu.querySelector("button");
                if (deleteButton) {
                  deleteButton.onclick = (evt) => {
                    evt.stopPropagation();
                    onDelete(app.id);
                    menu.remove();
                  };
                }
                
                // Remove menu when clicking outside
                const handleClickOutside = (evt: Event) => {
                  if (!menu.contains(evt.target as Node)) {
                    menu.remove();
                    document.removeEventListener("click", handleClickOutside);
                  }
                };
                setTimeout(() => document.addEventListener("click", handleClickOutside), 0);
              }
            }}
          >
            <MoreHorizontalIcon className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  const defaultSort = (a: Application, b: Application) => {
    // Default sort by last_activity descending
    const dateA = a.last_activity > 0 ? a.last_activity : 0;
    const dateB = b.last_activity > 0 ? b.last_activity : 0;
    return dateB - dateA;
  };

  return (
    <PaginatedTable
      data={applications}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      emptyTitle="No applications found"
      emptyDescription="Applications will appear here automatically when you start using Maitai."
      onRowClick={(app) => navigate(`/application/${app.id}`)}
      defaultSort={defaultSort}
    />
  );
};

export default ApplicationsTable; 